// 导航栏组件 - Tailwind CSS 现代简约风格
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Key, CreditCard, BarChart3, Layers, FileText, LogOut, Zap, Settings, Users, Activity } from 'lucide-react';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // 根据首字母获取头像背景颜色
  const getAvatarColor = (letter: string) => {
    const colors: { [key: string]: string } = {
      'A': 'bg-red-500', 'B': 'bg-orange-500', 'C': 'bg-amber-500', 'D': 'bg-yellow-600',
      'E': 'bg-lime-600', 'F': 'bg-green-500', 'G': 'bg-emerald-500', 'H': 'bg-teal-500',
      'I': 'bg-cyan-500', 'J': 'bg-sky-500', 'K': 'bg-blue-500', 'L': 'bg-indigo-500',
      'M': 'bg-violet-500', 'N': 'bg-purple-500', 'O': 'bg-fuchsia-500', 'P': 'bg-pink-500',
      'Q': 'bg-rose-500', 'R': 'bg-red-600', 'S': 'bg-orange-600', 'T': 'bg-amber-600',
      'U': 'bg-green-600', 'V': 'bg-teal-600', 'W': 'bg-blue-600', 'X': 'bg-indigo-600',
      'Y': 'bg-purple-600', 'Z': 'bg-pink-600'
    };
    return colors[letter.toUpperCase()] || 'bg-gray-500';
  };

  const handleLogout = () => {
    localStorage.removeItem('apiKey');
    navigate('/login');
  };

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const navItems = [
  { path: '/dashboard/models', label: 'Models', icon: Layers },
  { path: '/dashboard/keys', label: 'API Keys', icon: Key },
  { path: '/dashboard/usage', label: 'Usage', icon: BarChart3 },
  { path: '/dashboard/billing', label: 'Billing', icon: CreditCard },
  { path: '/dashboard/referrals', label: 'Referrals', icon: Users },
  { path: '/dashboard/documentation', label: 'Documentation', icon: FileText },
];

  return (
    <nav className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-8">
            {/* Logo部分 */}
            <div className="flex items-center space-x-3">
              <img src={`${process.env.PUBLIC_URL}/mplogo.png`} alt="ModelPool" className="h-6 w-6" />
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                ModelPool
              </h1>
            </div>

            {/* 导航菜单 */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium transition-all duration-300 relative ${isActive
                      ? 'text-blue-600 border-b-2 border-blue-500'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* 右侧用户操作 */}
          <div className="flex items-center relative">
            <div className="relative user-menu-container">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={`flex items-center justify-center w-8 h-8 text-white rounded-lg text-sm font-medium hover:opacity-80 transition-all duration-200 ${getAvatarColor(localStorage.getItem('username')?.charAt(0) || 'U')}`}
                title="User Menu"
              >
                {localStorage.getItem('username')?.charAt(0).toUpperCase() || 'U'}
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <Link
                    to="/dashboard/settings"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowUserMenu(false)}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 移动端导航菜单 */}
        <div className="md:hidden border-t border-gray-200 py-3">
          <div className="flex items-center space-x-1 overflow-x-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${isActive
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;